import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import type {  QueryResult } from '../graphql';
import { Obj } from '@livecontrol/core-utils';
import { Exception } from '@livecontrol/core-ui';
import { Asset } from '@livecontrol/scheduler/model';

interface TVariables {
  client_id: number;
  asset_id: number;
}

interface GetClipQueueResponse {
  error: boolean;
  clip_queue?: Asset.ClipQueue[];
  count?: number;
  page?: number;
  limit?: number;
  has_more?: boolean;
}

interface TData {
  getAssetClipQueue: GetClipQueueResponse;
}

interface Args {
  assetId: number;
  clientId: number;
}

// Extendemos el tipo QueryResult para incluir refetch
type ExtendedQueryResult<T, K extends string> = QueryResult<T, K> & {
  refetch: () => Promise<any>;
};

export const QUERY = gql`
  query GetAssetClipQueue($client_id: Int!, $asset_id: Int!) {
    getAssetClipQueue(client_id: $client_id, asset_id: $asset_id) {
      error
      clip_queue {
        id
        event_id
        asset {
          # Add AssetResponse fields here as needed
          id
          # ... other asset fields
        }
        assetId
        title
        description
        created_at
        updated_at
        started_at
        start_time
        end_time
        url
        fulfilled
        retry_count
        status
        hidden
        type
      }
      count
      page
      limit
      has_more
    }
  }
`;

export const useAssetClipQueue = (args: Args): ExtendedQueryResult<Asset.ClipQueue[], 'clipQueue'> => {
  const [error, setError] = useState<Error | undefined>();
  const [clipQueue, setClipQueue] = useState<Asset.ClipQueue[] | undefined>(undefined);

  const variables = {
    asset_id: args.assetId,
    client_id: args.clientId
  };

  if (!Obj.isHydrated(variables)) {
    return { 
      loading: false, 
      error: Exception.KABOOM,
      refetch: async () => Promise.resolve()
    };
  }

  /* eslint-disable react-hooks/rules-of-hooks */

  // Prepare the query - ahora obtenemos también refetch
  const { loading, data, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables,
    onError() {
      setError(Exception.KABOOM);
    }
  });

  useEffect(() => {
    let clipQueueData: Asset.ClipQueue[] | undefined;
    let error_;

    if (data?.getAssetClipQueue) {
      const response = data.getAssetClipQueue;

      if (!response.error) {
        clipQueueData = response.clip_queue || [];
      } else {
        error_ = new Error('Unable to get clip queue');
      }
    }

    // console.log('clipQueueData: ', clipQueueData);

    setClipQueue(clipQueueData);
    setError(error_);
  }, [data]);

  return { error, loading, clipQueue, refetch };
};